/**
 * ChatBackupPage - Settings sub-page for managing Chat Backup and Recovery Keys
 *
 * States:
 * A) No SSSS configured: Generate and display recovery key, require copy before activation
 * B) SSSS configured: Show active backup status with Export/Reset options
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useI18n } from '../../context/I18nContext';
import { useModal } from '../../context/ModalContext';
import { useToast } from '../../context/ToastContext';
import { useMatrixClient } from '../../context/MatrixContext';
import { useRecovery, useRecoveryActions } from '../../context/RecoveryContext/index';
import { useSecretStorage } from '../../hooks/useSecretStorage';
import { isRecoveryCompleted, markRecoveryCompleted, clearRecoveryCompletion } from '../../context/RecoveryContext/storage';
import { encodeRecoveryKey } from '../../constants/matrixEvents';
import {
  storeRecoveryKeyInSession,
  loadRecoveryKeyFromSession,
  getStorageKey,
  removeRecoveryKeyFromSession,
} from '../../utils/recoveryKeyStorage';
import logger from '../../utils/logger';
import { isUserAuthCancellation, AUTH_ERROR_MESSAGES } from '../../utils/authErrors';
import { fetchDisplayName } from '../../utils/profileUtils';
import { trustEventEmitter, TrustEvents } from '../../crypto/events';
import { getCachedPassword } from '../../services/authCredentialService';
import { openSSOFallbackAuth } from '../../services/ssoService';
import matrixAuthService from '../../services/matrixAuthService';
import * as credentialStore from '../../services/credentialStore';
import PasswordInputModal from '../Modal/PasswordInputModal';
import './ChatBackupPage.css';

const ChatBackupPage = ({ onBack, onShowRecoveryKeyModal }) => {
  const { t } = useI18n();
  const { showConfirm, showError } = useModal();
  const { showToast } = useToast();
  const client = useMatrixClient();
  const recoveryState = useRecovery();
  const recoveryActions = useRecoveryActions();
  const { bootstrap, isLoading: ssssLoading } = useSecretStorage();

  // State
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [hasSSSSConfigured, setHasSSSSConfigured] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);
  const [keyBytes, setKeyBytes] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [activationError, setActivationError] = useState(null);
  const [isResetting, setIsResetting] = useState(false);
  const [keyUnavailable, setKeyUnavailable] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [resetConfirmed, setResetConfirmed] = useState(false); // Triggers reset after confirm dialog closes
  const [detectionError, setDetectionError] = useState(null); // Error from recovery detection (e.g., timeout)

  // Two-phase reset: store pending key until user confirms by copying/exporting
  const [pendingResetKey, setPendingResetKey] = useState(null);
  const [pendingResetKeyBytes, setPendingResetKeyBytes] = useState(null);

  // SSO re-auth state
  const [isSSOAuthInProgress, setIsSSOAuthInProgress] = useState(false);
  const ssoAbortRef = useRef(null);
  const bootstrapAbortRef = useRef(null);

  // Password dialog state for UIA
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
  const passwordResolveRef = useRef(null);

  // Refs
  const mountedRef = useRef(true);
  const bootstrapInProgressRef = useRef(false);
  const showingNewKeyRef = useRef(false);  // Guards against checkStatus overriding key display during reset
  // Guards openRecoveryKeyEntry to run at most once per mount. Intentionally never reset: opening the
  // modal sets currentPage='settings' which unmounts this page, so the ref dies with the component and
  // a fresh mount starts at false.
  const openingRecoveryRef = useRef(false);

  // Check if recovery was already completed for this device
  const hasCompletedRecovery = useCallback(() => {
    if (!client) return false;
    const deviceId = client.getDeviceId();
    return deviceId ? isRecoveryCompleted(deviceId) : false;
  }, [client]);

  /**
   * Generate recovery key data without updating state
   * Used by both initial setup and reset flows to generate before bootstrap
   * @returns {{ key: string, keyBytes: Uint8Array }} Generated key data
   */
  const generateKeyData = useCallback(() => {
    logger.debug('[ChatBackupPage] Generating new recovery key...');

    // Generate 32 random bytes
    const newKeyBytes = new Uint8Array(32);
    crypto.getRandomValues(newKeyBytes);

    // Encode to base58 format
    const keyString = encodeRecoveryKey(newKeyBytes);

    logger.debug('[ChatBackupPage] Recovery key generated');
    return { key: keyString, keyBytes: newKeyBytes };
  }, []);

  /**
   * Generate new recovery key and update state (legacy, used for error fallback)
   */
  const generateRecoveryKey = useCallback(async () => {
    try {
      const { key, keyBytes: newKeyBytes } = generateKeyData();

      if (mountedRef.current) {
        setKeyBytes(newKeyBytes);
        setGeneratedKey(key);
        setIsCopied(false);
        setActivationError(null);
      }
    } catch (err) {
      logger.error('[ChatBackupPage] Failed to generate key:', err);
      if (mountedRef.current) {
        setActivationError(t('ChatBackupPage.errorGeneration'));
      }
    }
  }, [generateKeyData, t]);

  /**
   * Cross-sign the current device after backup setup
   * @param {string} logContext - Context for log messages (e.g., "via export")
   */
  const crossSignCurrentDevice = useCallback(async (logContext = '') => {
    const crypto = client?.getCrypto?.();
    const deviceId = client?.getDeviceId();
    const userId = client?.getUserId();

    if (!crypto || !deviceId || !userId) return;

    const contextSuffix = logContext ? ` (${logContext})` : '';
    logger.debug(`[ChatBackupPage] Cross-signing current device after backup setup${contextSuffix}`);

    try {
      // CRITICAL FIX: Ensure cross-signing private keys are loaded from SSSS into crypto module
      // Without this, crossSignDevice() silently fails because the self-signing key isn't available
      // This mirrors what FluffyChat does after recovery key entry
      if (crypto.bootstrapCrossSigning) {
        logger.debug('[ChatBackupPage] Loading cross-signing keys from SSSS into crypto module');
        try {
          await crypto.bootstrapCrossSigning({
            setupNewCrossSigning: false, // Don't create new, just load existing from SSSS
            authUploadDeviceSigningKeys: async (makeRequest) => {
              // May need UIA for key upload, but usually not for just loading
              try {
                await makeRequest();
              } catch (e) {
                // Only ignore UIA-related errors (M_FORBIDDEN or interactive auth flows)
                // Re-throw other errors (network, server errors) so they're handled properly
                if (e.errcode === 'M_FORBIDDEN' || e.data?.flows) {
                  logger.debug('[ChatBackupPage] Skipping UIA for key loading:', e.message);
                } else {
                  logger.debug('[ChatBackupPage] authUploadDeviceSigningKeys error:', e.message);
                  throw e;
                }
              }
            },
          });
          logger.debug('[ChatBackupPage] Cross-signing keys loaded successfully');
        } catch (loadError) {
          // Log but continue - keys may already be cached
          logger.debug('[ChatBackupPage] bootstrapCrossSigning returned:', loadError.message);
        }
      }

      if (crypto.crossSignDevice) {
        await crypto.crossSignDevice(deviceId);
        logger.debug('[ChatBackupPage] Device cross-signed successfully');
      }

      const status = await crypto.getDeviceVerificationStatus?.(userId, deviceId);
      if (!status?.signedByOwner && crypto.setDeviceVerified) {
        await crypto.setDeviceVerified(userId, deviceId);
        logger.debug('[ChatBackupPage] Device marked as verified (fallback)');
      }

      const finalStatus = await crypto.getDeviceVerificationStatus?.(userId, deviceId);
      logger.debug('[ChatBackupPage] Final device verification status:', {
        signedByOwner: finalStatus?.signedByOwner,
        crossSigningVerified: finalStatus?.crossSigningVerified,
      });

      // Emit event if device is actually verified so hooks can refetch
      if (finalStatus?.signedByOwner || finalStatus?.crossSigningVerified) {
        trustEventEmitter.emitTrustEvent(TrustEvents.DEVICE_TRUSTED, {
          userId,
          deviceId,
        });
        logger.debug('[ChatBackupPage] Device trust event emitted');
      }
    } catch (verifyError) {
      logger.warn('[ChatBackupPage] Device verification failed:', verifyError.message);
    }
  }, [client]);

  /**
   * Handle password required for UIA during cross-signing bootstrap
   * Returns a promise that resolves with the password or null if cancelled
   * @param {string} session - UIA session ID
   * @param {string} errorKey - Error key if this is a re-prompt (e.g., 'incorrectPassword')
   */
  const handlePasswordRequired = useCallback((session, errorKey) => {
    // Try cached password first (from recent login, expires after 10 mins)
    // Only use cache if this isn't a retry after wrong password
    if (!errorKey) {
      const cachedPassword = getCachedPassword();
      if (cachedPassword) {
        logger.debug('[ChatBackupPage] Using cached password for UIA');
        return Promise.resolve(cachedPassword);
      }
    }

    // No cached password or this is a retry - show dialog
    return new Promise((resolve) => {
      passwordResolveRef.current = resolve;
      // Re-enable submit button for retry
      setIsPasswordSubmitting(false);
      // Set error message if this is a re-prompt due to wrong password
      if (errorKey === 'incorrectPassword') {
        setPasswordError(t('ChatBackupPage.incorrectPassword'));
      } else {
        setPasswordError(null);
      }
      setShowPasswordDialog(true);
    });
  }, [t]);  // Removed showPasswordDialog - it was causing useEffect cleanup to run when dialog opens

  /**
   * Handle SSO re-authentication required for UIA during cross-signing bootstrap
   * Opens a popup to the Matrix SSO fallback endpoint, waits for completion
   * @param {string} session - UIA session ID
   */
  const handleSsoRequired = useCallback(async (session) => {
    if (!mountedRef.current) throw new Error(AUTH_ERROR_MESSAGES.ABORTED);
    logger.info('[ChatBackupPage] SSO re-authentication required for UIA');
    setIsSSOAuthInProgress(true);
    const controller = new AbortController();
    ssoAbortRef.current = controller;
    try {
      // Read homeserver and login hint from stored credentials
      let homeserverUrl;
      let loginHint;
      try {
        const creds = credentialStore.readCredentials();
        homeserverUrl = creds?.homeserverUrl;
        // Prefer the original SSO login email (full email) over Matrix ID localpart
        loginHint = localStorage.getItem('ssoLoginEmail') || undefined;
      } catch {
        // Fall back to defaults
      }
      await openSSOFallbackAuth(session, controller.signal, homeserverUrl, loginHint);
      logger.info('[ChatBackupPage] SSO re-authentication popup closed — onSsoRequired resolving');
    } catch (ssoErr) {
      logger.error('[ChatBackupPage] SSO re-authentication failed:', ssoErr.message);
      throw ssoErr;
    } finally {
      ssoAbortRef.current = null;
      if (mountedRef.current) setIsSSOAuthInProgress(false);
    }
  }, []);

  /**
   * Retry detection after a timeout error
   * Re-triggers recovery detection via matrixAuthService
   */
  const handleRetryDetection = useCallback(async () => {
    if (!client) {
      logger.warn('[ChatBackupPage] Cannot retry detection - no client');
      return;
    }

    logger.info('[ChatBackupPage] Retrying recovery detection...');
    setDetectionError(null);
    setIsCheckingStatus(true);

    try {
      // Import dynamically to avoid circular dependency
      const matrixAuthService = (await import('../../services/matrixAuthService')).default;
      await matrixAuthService.detectRecoveryRequirement(client);
      // State will be updated via recoveryActions.init callback
      // The useEffect will re-run when recoveryState changes
    } catch (err) {
      logger.error('[ChatBackupPage] Retry detection failed:', err);
      setDetectionError({ code: 'RETRY_FAILED', message: err.message });
      setIsCheckingStatus(false);
    }
  }, [client]);

  /**
   * NED-4003: server backup exists but THIS device is no longer verified (e.g. another device reset
   * the recovery key). Surface the EXISTING recovery-key modal — the same one shown when a user skips
   * the key at login and enters it later — instead of a bespoke in-page panel.
   *
   * The stale per-device "recovery completed" marker (and a lingering step===DONE) would otherwise
   * make RecoveryKeyModal render its success/Export state rather than the key INPUT, so:
   *   1. clearRecoveryCompletion() — this device is genuinely no longer recovered.
   *   2. re-run detection — its init side effect flips recoveryState → needsRecovery + step=AWAIT_KEY,
   *      which is what makes the modal show the INPUT. detectRecoveryRequirement returns void; the
   *      result reaches React only via recoveryActions.init — do NOT gate on a return value. (A
   *      detection timeout/error triggers the pre-existing logout in RecoveryProvider.handleRecoveryInit.)
   *   3. onShowRecoveryKeyModal() — SettingsModal renders its existing overlay and returns to the
   *      Settings list underneath, so closing/skipping the modal never exposes the State-A surface.
   */
  const openRecoveryKeyEntry = useCallback(async () => {
    // Run at most once per mount. checkStatus can re-run (the re-detect dispatch flips recoveryState
    // deps such as hasBackup) and re-enter an unverified branch before this page unmounts, which would
    // otherwise fire a redundant second detect / onShowRecoveryKeyModal. Re-entries early-return here;
    // the first (owning) call's continuation below is what opens the modal, so suppressing re-entries
    // never drops it.
    if (openingRecoveryRef.current) {
      return;
    }
    openingRecoveryRef.current = true;
    logger.info('[ChatBackupPage] Device unverified with server backup — opening existing recovery-key modal');
    clearRecoveryCompletion();
    try {
      await matrixAuthService.detectRecoveryRequirement(client);
    } catch (err) {
      logger.warn('[ChatBackupPage] re-detect before recovery modal failed:', err?.message || err);
    }
    if (mountedRef.current) {
      onShowRecoveryKeyModal?.();
    }
  }, [client, onShowRecoveryKeyModal]);

  // Check SSSS status on mount and bootstrap if needed
  useEffect(() => {
    // Reset mountedRef on each effect run to prevent stale state after effect re-runs
    // Without this, effect cleanup sets mountedRef=false, blocking state updates in finally blocks
    mountedRef.current = true;

    const checkStatus = async () => {
      // Guard against concurrent bootstrap (React 18 StrictMode runs effects twice)
      if (bootstrapInProgressRef.current) {
        logger.debug('[ChatBackupPage] Bootstrap already in progress, skipping...');
        return;
      }

      // Guard against re-running during reset flow when we're showing the new key
      // Check ref FIRST - it's synchronous and always current (no stale closure issues)
      if (showingNewKeyRef.current) {
        logger.debug('[ChatBackupPage] Showing new key from reset, skipping status check');
        setIsCheckingStatus(false);
        return;
      }

      // If we already have a generated key (from bootstrap in this session), don't re-check
      // This prevents the storedKey path from overriding the key display after bootstrap
      // (storedKey path sets hasSSSSConfigured=true, which hides the key the user needs to save)
      if (generatedKey) {
        logger.debug('[ChatBackupPage] Already have generated key, skipping status check');
        setIsCheckingStatus(false);
        return;
      }

      try {
        logger.debug('[ChatBackupPage] Checking SSSS status...');

        // Check if we're coming from a reset flow (RecoveryKeyModal's "Recovery key lost?" → Confirm)
        // Two-phase flow: Generate key in memory (Phase 1), wait for copy/export to commit (Phase 2)
        const forceKeyGen = sessionStorage.getItem('forceKeyGeneration');
        if (forceKeyGen === 'true') {
          logger.info('[ChatBackupPage] Force key generation flag detected - Phase 1: generating key in memory');
          sessionStorage.removeItem('forceKeyGeneration');
          setHasSSSSConfigured(false);

          // Phase 1: Generate key in memory only - don't bootstrap yet
          // The key will be committed to server when user clicks Copy/Export (Phase 2)
          const { key: newKey, keyBytes: newKeyBytes } = generateKeyData();

          // Set as pending reset key - this tells handleCopyToClipboard/handleExportAndActivate
          // to call commitPendingReset() which does the actual bootstrap
          showingNewKeyRef.current = true;
          if (mountedRef.current) {
            setPendingResetKey(newKey);
            setPendingResetKeyBytes(newKeyBytes);
            setGeneratedKey(newKey);
            setKeyBytes(newKeyBytes);
            setIsCheckingStatus(false);
          }
          logger.info('[ChatBackupPage] Phase 1 complete - key displayed, waiting for user to copy/export');
          return;
        }

        // Check for detection error (e.g., timeout) - NEVER auto-bootstrap when server state unknown
        if (recoveryState?.error) {
          logger.warn('[ChatBackupPage] Recovery detection failed:', recoveryState.error);
          setDetectionError(recoveryState.error);
          setIsCheckingStatus(false);
          return; // Don't proceed to bootstrap - show retry UI instead
        }

        // Check from recovery state (server-side state is authoritative)
        const hasSSSS = recoveryState?.hasSSSS;
        const hasBackup = recoveryState?.hasBackup;
        const completed = hasCompletedRecovery();

        // Check if detection has completed. After INIT action, hasSSSS will be boolean.
        // If it's undefined, detection hasn't run yet - wait for it.
        const detectionComplete = typeof hasSSSS === 'boolean';

        logger.debug('[ChatBackupPage] Status from recoveryState:', {
          hasSSSS,
          hasBackup,
          completed,
          detectionComplete,
          fullRecoveryState: JSON.stringify(recoveryState),
        });
        logger.debug('[ChatBackupPage] Status:', { hasSSSS, hasBackup, completed, detectionComplete });

        // Determine whether THIS device is verified — the same signal the Devices page uses
        // (signedByOwner || crossSigningVerified). Distinguish a DEFINITIVE "unverified" from a
        // "couldn't read" so a transient crypto hiccup never downgrades a verified/completed device.
        // A server backup existing (hasBackup, via getKeyBackupInfo) does NOT mean THIS device can
        // use it; a dehydrated/unverified device must not show "Chat Backup Active".
        // [A_25514 regression / UC12X.01.01]
        let deviceVerified = false;
        let deviceVerificationKnown = false;
        try {
          const crypto = client?.getCrypto?.();
          const uid = client?.getUserId?.();
          const did = client?.getDeviceId?.();
          if (crypto && uid && did) {
            const vs = await crypto.getDeviceVerificationStatus(uid, did);
            if (vs) {
              deviceVerificationKnown = true;
              deviceVerified = !!(vs.signedByOwner || vs.crossSigningVerified);
            }
          }
        } catch (verifyErr) {
          logger.warn('[ChatBackupPage] device verification check failed; treating as unknown:', verifyErr?.message || verifyErr);
        }
        const deviceDefinitelyUnverified = deviceVerificationKnown && !deviceVerified;

        // GUARD: Check completed FIRST, before detectionComplete.
        // The localStorage completion flag is the most reliable indicator that recovery finished for
        // this device — BUT it is only authoritative while the device is still verified. A device
        // that completed recovery and later lost verification (cross-signing reset / rehydration
        // reusing the same device id, no logout) must NOT show a stale "Chat Backup Active".
        if (completed && !deviceDefinitelyUnverified) {
          logger.info('[ChatBackupPage] Recovery completed for device (localStorage flag set) and device verified — showing State B');
          setHasSSSSConfigured(true);
          if (mountedRef.current) {
            setIsCheckingStatus(false);
          }
          return;
        }

        // Wait for detection to complete before making any decisions
        // This prevents race condition where bootstrap runs before we know server state
        if (!detectionComplete) {
          logger.debug('[ChatBackupPage] Detection not yet complete, waiting...');
          // Keep loading state - effect will re-run when recoveryState changes
          return;
        }

        if (hasBackup && !deviceDefinitelyUnverified) {
          logger.debug('[ChatBackupPage] Setting hasSSSSConfigured=true (server backup exists and this device is verified)');
          setHasSSSSConfigured(true);
          if (mountedRef.current) {
            setIsCheckingStatus(false);
          }
        } else if (hasBackup && deviceDefinitelyUnverified) {
          // Server backup exists but THIS device is unverified. Do NOT show "Chat Backup Active"
          // (misleading) and do NOT generate a new key (committing it would RESET/destroy the
          // existing server backup). Surface the EXISTING recovery-key modal so the user can type
          // their key and re-verify this device WITHOUT logging out. Keep the spinner during the
          // awaited re-detect; onShowRecoveryKeyModal then navigates back to the Settings list
          // (unmounting this page), so the State-A surface is never exposed. Once verified,
          // re-opening this page re-runs detection → State B.
          logger.info('[ChatBackupPage] Server backup exists but THIS device is unverified — opening recovery-key modal');
          await openRecoveryKeyEntry();
          return;
        } else {
          // Defense-in-depth (A_25514): recoveryState reports no backup, but re-confirm directly
          // against the server before doing anything destructive. The State-A path below generates a
          // key and (on copy/export) bootstraps secret storage, which RESETS any existing server
          // backup. If detection was stale/failed and a backup actually exists, that would silently
          // destroy it. Route by THIS device's verification (already computed above): unverified →
          // recovery-key modal; verified/unknown → "Chat Backup Active" (never prompt a verified user).
          try {
            const crypto = client?.getCrypto?.();
            if (crypto && typeof crypto.getKeyBackupInfo === 'function') {
              const serverBackup = await crypto.getKeyBackupInfo();
              if (serverBackup && deviceDefinitelyUnverified) {
                logger.warn('[ChatBackupPage] recoveryState reported no backup but server HAS one and THIS device is unverified — opening recovery-key modal (avoiding destructive reset)');
                await openRecoveryKeyEntry();
                return;
              }
              if (serverBackup) {
                logger.warn('[ChatBackupPage] recoveryState reported no backup but server HAS one and THIS device is verified/unknown — showing Chat Backup Active (avoiding destructive reset)');
                setHasSSSSConfigured(true);
                if (mountedRef.current) {
                  setIsCheckingStatus(false);
                }
                return;
              }
            }
          } catch (backupCheckErr) {
            // The re-check itself failed — we now do NOT know whether a server backup exists. On a
            // DESTRUCTIVE path, "unknown" must block, not proceed: falling through would generate a
            // key whose commit could reset a real backup. Show the non-destructive retry surface
            // instead (mirrors how a recoveryState detection error is handled above).
            logger.warn('[ChatBackupPage] Direct server backup re-check FAILED — not generating a new key (unknown backup state); showing retry surface:', backupCheckErr?.message || backupCheckErr);
            setDetectionError({ code: 'BACKUP_RECHECK_FAILED', message: backupCheckErr?.message || 'backup re-check failed' });
            if (mountedRef.current) {
              setIsCheckingStatus(false);
            }
            return;
          }

          logger.debug('[ChatBackupPage] Server has no SSSS/backup, proceeding to initial setup...');
          // Server definitively has no SSSS/backup according to recoveryState
          // This is authoritative - any stored sessionStorage key is stale (e.g., from before server reset)
          // Clear stale data and proceed to bootstrap fresh
          const userId = client?.getUserId();
          if (userId) {
            // Clear any stale stored key from previous session before server reset
            const storedKey = await loadRecoveryKeyFromSession(userId).catch(() => null);
            if (storedKey) {
              logger.info('[ChatBackupPage] Clearing stale stored recovery key (server has no SSSS)');
              removeRecoveryKeyFromSession(userId);
            }
          }

          // Clear any stale local completion marker (e.g., after server-side reset)
          if (completed) {
            logger.info('[ChatBackupPage] Clearing stale recovery completion marker (server has no SSSS)');
            clearRecoveryCompletion();
          }

          // Initial setup: Phase 1 - generate key in memory, show to user, don't bootstrap yet
          // Phase 2 happens when user copies/exports via commitPendingReset()
          // This ensures backup is only activated AFTER user has saved their recovery key
          setHasSSSSConfigured(false);
          logger.debug('[ChatBackupPage] No existing SSSS, starting initial setup (Phase 1)...');

          const { key: newKey, keyBytes: newKeyBytes } = generateKeyData();

          // Phase 1: Generate key in memory only - don't bootstrap yet
          // The key will be committed to server when user clicks Copy/Export (Phase 2)
          showingNewKeyRef.current = true;
          if (mountedRef.current) {
            setPendingResetKey(newKey);
            setPendingResetKeyBytes(newKeyBytes);
            setGeneratedKey(newKey);
            setKeyBytes(newKeyBytes);
            setIsCheckingStatus(false);
          }
          logger.info('[ChatBackupPage] Phase 1 complete - key displayed, waiting for user to copy/export');
        }
      } catch (err) {
        logger.error('[ChatBackupPage] Error checking status:', err);
        setHasSSSSConfigured(false);
        // Fallback: just show key generation UI
        await generateRecoveryKey();
        if (mountedRef.current) {
          setIsCheckingStatus(false);
        }
      }
    };

    checkStatus();

    return () => {
      mountedRef.current = false;
      // NOTE: Do NOT clear generatedKey/keyBytes here!
      // This cleanup runs when effect re-runs (due to recoveryState changes),
      // not just on unmount. Clearing state here causes the key display bug.
      // Sensitive data is cleared when component unmounts (separate effect below).
      // Resolve pending password promise on effect cleanup
      if (passwordResolveRef.current) {
        passwordResolveRef.current(null);
        passwordResolveRef.current = null;
      }
    };
  // Note: showingNewKeyRef is intentionally not in deps - refs are stable and don't trigger re-renders
  }, [recoveryState?.hasSSSS, recoveryState?.hasBackup, recoveryState?.error, hasCompletedRecovery,
      generateKeyData, generateRecoveryKey, bootstrap, crossSignCurrentDevice,
      handlePasswordRequired, client, t]);

  // Separate cleanup effect that only runs on actual unmount (empty dependency array)
  useEffect(() => {
    return () => {
      // Abort any in-progress SSO popup
      if (ssoAbortRef.current) ssoAbortRef.current.abort();
      // Abort any in-progress bootstrap/retry loop
      if (bootstrapAbortRef.current) bootstrapAbortRef.current.abort();
      // Clear sensitive data from memory on component unmount only
      setGeneratedKey(null);
      setKeyBytes(null);
      setPendingResetKey(null);
      setPendingResetKeyBytes(null);
      showingNewKeyRef.current = false;  // Reset the guard on unmount
    };
  }, []);

  // Load encrypted recovery key from sessionStorage when SSSS is configured
  useEffect(() => {
    const loadEncryptedKey = async () => {
      // Only load if SSSS is configured and we don't already have a key
      if (!hasSSSSConfigured || generatedKey || !client) {
        return;
      }

      const userId = client.getUserId();
      if (!userId) {
        return;
      }

      try {
        const decryptedKey = await loadRecoveryKeyFromSession(userId);
        if (mountedRef.current && decryptedKey) {
          setGeneratedKey(decryptedKey);
          setKeyUnavailable(false);
          logger.debug('[ChatBackupPage] Loaded recovery key from sessionStorage');
        } else if (mountedRef.current) {
          // Key not available - export will remain disabled
          setKeyUnavailable(true);
        }
      } catch (err) {
        logger.warn('[ChatBackupPage] Failed to load stored key:', err.message);
        // Key not available - export will remain disabled, show user message
        if (mountedRef.current) {
          setKeyUnavailable(true);
        }
      }
    };

    loadEncryptedKey();
  }, [hasSSSSConfigured, generatedKey, client]);

  /**
   * Handle password submission from dialog
   */
  const handlePasswordSubmit = useCallback((password) => {
    // Disable submit button to prevent multiple clicks
    setIsPasswordSubmitting(true);
    setShowPasswordDialog(false);
    setPasswordError(null);
    if (passwordResolveRef.current) {
      passwordResolveRef.current(password);
      passwordResolveRef.current = null;
    }
  }, []);

  /**
   * Handle password dialog cancel
   */
  const handlePasswordCancel = useCallback(() => {
    setShowPasswordDialog(false);
    setPasswordError(null);
    setIsPasswordSubmitting(false);
    if (passwordResolveRef.current) {
      passwordResolveRef.current(null);
      passwordResolveRef.current = null;
    }
  }, []);

  /**
   * Phase 2: Commit the pending key to server
   * Called when user copies or exports the new key
   * Handles both:
   * - Initial setup (no existing backup): Just bootstrap
   * - Reset flow (existing backup): Delete old keys first, then bootstrap
   */
  const commitPendingReset = useCallback(async () => {
    if (!pendingResetKey || !pendingResetKeyBytes) {
      return; // No pending key to commit
    }

    // Determine if this is a reset (has existing backup) or initial setup (no backup)
    const isResetFlow = recoveryState?.hasBackup;
    const flowType = isResetFlow ? 'reset' : 'initial setup';
    logger.info(`[ChatBackupPage] Phase 2: Committing ${flowType} to server...`);

    try {
      const userId = client?.getUserId();

      // Step 1: Delete old keys from server (ONLY for reset flow, not initial setup)
      if (isResetFlow) {
        if (recoveryActions?.reset) {
          logger.debug('[ChatBackupPage] Deleting old keys from server...');
          await recoveryActions.reset();
        }

        // Step 2: Clear stored key (only for reset - initial setup has no stored key)
        if (userId) {
          removeRecoveryKeyFromSession(userId);
        }
      }

      // Step 3: Bootstrap new keys
      // For initial setup: No password required per MSC3967
      // For reset: Password required for UIA
      logger.debug(`[ChatBackupPage] Bootstrapping new keys (${flowType})...`);
      const abortController = new AbortController();
      bootstrapAbortRef.current = abortController;
      try {
        await bootstrap(pendingResetKeyBytes, {
          onPasswordRequired: handlePasswordRequired,
          onSsoRequired: handleSsoRequired,
          signal: abortController.signal,
        });
      } finally {
        bootstrapAbortRef.current = null;
      }

      logger.info(`[ChatBackupPage] SSSS bootstrapped successfully (${flowType})`);

      // Step 4: Cross-sign current device (ONLY for reset flow)
      // For initial setup, bootstrapCrossSigning({ setupNewCrossSigning: true }) already
      // signs the device as part of key creation. Calling crossSignCurrentDevice again
      // causes the SDK to create NEW keys (requiring UIA) that invalidate the signature.
      if (isResetFlow) {
        await crossSignCurrentDevice('after reset');
      }

      // Step 5: Mark recovery as completed
      const deviceId = client?.getDeviceId();
      if (deviceId) {
        markRecoveryCompleted(deviceId);
        logger.debug('[ChatBackupPage] Marked recovery as completed for device:', deviceId);
      }

      // Step 6: Store key for later export
      if (userId && pendingResetKey) {
        await storeRecoveryKeyInSession(pendingResetKey, userId);
      }

      // Clear pending state - commit is done
      setPendingResetKey(null);
      setPendingResetKeyBytes(null);

      // Re-detect recovery state to update hasBackup in context (for initial setup)
      // This ensures UI shows correct state on navigation back
      if (!isResetFlow) {
        await matrixAuthService.detectRecoveryRequirement(client);
        logger.info('[ChatBackupPage] Recovery state re-detected after initial bootstrap');
      }

      logger.info(`[ChatBackupPage] Phase 2 complete - ${flowType} committed to server`);

    } catch (err) {
      // Check if user cancelled password/SSO entry
      if (isUserAuthCancellation(err)) {
        logger.info('[ChatBackupPage] Commit classified as user auth cancellation:', err.message);
        // Don't show error for user cancellation, but keep pending state
        // so user can try again
      } else {
        logger.error('[ChatBackupPage] Commit failed:', err.message, {
          httpStatus: err.httpStatus,
          errcode: err.errcode,
          data: err.data ? JSON.stringify(err.data) : undefined,
        });
        // Don't showError here — caller (handleCopyToClipboard/handleExportKey) handles it
      }
      throw err; // Re-throw so caller knows commit failed
    }
  }, [pendingResetKey, pendingResetKeyBytes, client, recoveryState?.hasBackup, recoveryActions, bootstrap,
      handlePasswordRequired, handleSsoRequired, crossSignCurrentDevice, showError, t]);

  /**
   * Copy recovery key to clipboard
   * If this is a pending key (initial setup or reset), commits to server first
   */
  const handleCopyToClipboard = useCallback(async () => {
    if (!generatedKey) return;

    setIsActivating(true);
    try {
      // Copy to clipboard FIRST while user-gesture context is still valid.
      // commitPendingReset() takes several seconds of async server work
      // (SSSS bootstrap, cross-signing, UIA, etc.), which causes the
      // browser to revoke the transient user-activation needed by the
      // Clipboard API.
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(generatedKey);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = generatedKey;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      logger.info('[ChatBackupPage] Recovery key copied to clipboard');

      // Show copy success animation on the button immediately.
      // Note: this confirms the clipboard write succeeded. If commitPendingReset()
      // fails afterward, the error modal still appears, but the user will have
      // briefly seen the checkmark. This is intentional — the alternative is
      // losing the user-gesture context and failing the clipboard write entirely.
      setShowCopySuccess(true);
      setTimeout(() => {
        if (mountedRef.current) {
          setShowCopySuccess(false);
        }
      }, 2000);

      // If this is a pending key (initial setup or reset), commit to server after copying
      if (pendingResetKey) {
        await commitPendingReset();
      }

      // Mark activation complete only after server commit succeeds
      setIsCopied(true);

    } catch (err) {
      setShowCopySuccess(false); // Clear success state so button is re-enabled for retry
      if (isUserAuthCancellation(err)) {
        logger.info('[ChatBackupPage] Copy aborted due to auth:', err.message);
      } else {
        logger.error('[ChatBackupPage] Copy failed:', err);
        if (mountedRef.current) {
          showError(
            t('ChatBackupPage.title'),
            t('ChatBackupPage.errorCopy'),
          );
        }
      }
    } finally {
      if (mountedRef.current) {
        setIsActivating(false);
      }
    }
  }, [generatedKey, pendingResetKey, commitPendingReset, showError, t]);

  /**
   * Export recovery key to file
   * If this is a pending key (initial setup or reset), commits to server first
   */
  const handleExportAndActivate = useCallback(async () => {
    if (!generatedKey) return;

    setIsActivating(true);
    try {
      // If this is a pending key (initial setup or reset), commit it now before exporting
      if (pendingResetKey) {
        await commitPendingReset();
      }

      const userId = client?.getUserId();
      const displayName = await fetchDisplayName(client, userId);

      const exportData = {
        mxid: userId || '',
        displayName,
        recoveryKey: generatedKey,
        exportedAt: new Date().toISOString(),
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ti-messenger-recovery-key.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      logger.info('[ChatBackupPage] Recovery key exported to file');

      // Mark as saved (similar to copy)
      setIsCopied(true);

    } catch (err) {
      if (isUserAuthCancellation(err)) {
        logger.info('[ChatBackupPage] Export aborted due to auth:', err.message);
      } else {
        logger.error('[ChatBackupPage] Export failed:', err);
        if (mountedRef.current) {
          showError(
            t('ChatBackupPage.title'),
            t('ChatBackupPage.errorExport'),
          );
        }
      }
    } finally {
      if (mountedRef.current) {
        setIsActivating(false);
      }
    }
  }, [generatedKey, pendingResetKey, commitPendingReset, client, showError, t]);

  /**
   * Export recovery key as JSON file
   */
  const handleExportKey = useCallback(() => {
    showConfirm(
      t('ChatBackupPage.exportWarningTitle'),
      t('ChatBackupPage.exportWarningMessage'),
      async () => {
        try {
          const userId = client?.getUserId();
          const displayName = await fetchDisplayName(client, userId);

          // Try to get stored key from sessionStorage
          const encryptedKey = sessionStorage.getItem(getStorageKey(userId));
          const recoveryKeyToExport = generatedKey;

          if (encryptedKey && !recoveryKeyToExport) {
            // Key is encrypted in sessionStorage, need to decrypt
            // For now, show info that key needs to be re-entered
            showError(
              t('ChatBackupPage.title'),
              t('ChatBackupPage.keyNotAvailable'),
            );
            return;
          }

          if (!recoveryKeyToExport) {
            showError(
              t('ChatBackupPage.title'),
              t('ChatBackupPage.keyNotAvailable'),
            );
            return;
          }

          const exportData = {
            mxid: userId || '',
            displayName,
            recoveryKey: recoveryKeyToExport,
            exportedAt: new Date().toISOString(),
          };

          const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'ti-messenger-recovery-key.json';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);

          showToast({
            type: 'success',
            title: t('ChatBackupPage.title'),
            message: t('ChatBackupPage.exportSuccess'),
            countdown: 3,
            position: 'top-left',
          });
        } catch (err) {
          logger.error('[ChatBackupPage] Export failed:', err);
          showError(
            t('ChatBackupPage.title'),
            t('ChatBackupPage.errorExport'),
          );
        }
      },
    );
  }, [client, generatedKey, showConfirm, showError, showToast, t]);

  /**
   * Reset recovery key - shows confirm dialog, then triggers reset via state
   * Using state-based approach so confirm dialog closes before password dialog opens
   */
  const handleResetKey = useCallback(() => {
    showConfirm(
      t('ChatBackupPage.resetWarningTitle'),
      t('ChatBackupPage.resetWarningMessage'),
      () => {
        // Just set the flag - actual reset happens in useEffect
        // This allows the confirm dialog to close before password dialog opens
        setResetConfirmed(true);
      },
    );
  }, [showConfirm, t]);

  /**
   * Effect to perform Phase 1 of reset: generate new key and show it
   * The actual server-side reset happens in commitPendingReset when user copies/exports
   * Separated from handleResetKey to prevent modal eclipsing
   */
  useEffect(() => {
    if (!resetConfirmed) return;

    const performResetPhase1 = async () => {
      setIsResetting(true);
      setResetConfirmed(false); // Clear flag immediately

      try {
        logger.info('[ChatBackupPage] Phase 1: Generating new recovery key (not committed yet)...');

        // Generate new key in memory only - DO NOT commit to server yet
        const { key: newKey, keyBytes: newKeyBytes } = generateKeyData();

        // Store as pending - will be committed when user copies/exports
        setPendingResetKey(newKey);
        setPendingResetKeyBytes(newKeyBytes);

        // Show the new key to user (but old key is still active on server)
        showingNewKeyRef.current = true;
        if (mountedRef.current) {
          setGeneratedKey(newKey);
          setKeyBytes(newKeyBytes);
          setHasSSSSConfigured(false); // Show State A with key visible
          setIsCopied(false);
        }

        logger.info('[ChatBackupPage] Phase 1 complete - new key displayed, waiting for user to save');

      } catch (err) {
        logger.error('[ChatBackupPage] Reset Phase 1 failed:', err);
        showError(
          t('ChatBackupPage.title'),
          t('ChatBackupPage.errorReset'),
        );
      } finally {
        if (mountedRef.current) {
          setIsResetting(false);
        }
      }
    };

    performResetPhase1();
  }, [resetConfirmed, generateKeyData, showError, t]);

  // Handle back navigation - cancel pending setup/reset if user hasn't saved the key
  const handleBack = useCallback(() => {
    if (pendingResetKey) {
      // User is cancelling without saving the key
      // For initial setup: no backup gets created (user can retry later)
      // For reset: old key remains active on server
      logger.info('[ChatBackupPage] User cancelled without saving - clearing pending key');
      setPendingResetKey(null);
      setPendingResetKeyBytes(null);
      setGeneratedKey(null);
      setKeyBytes(null);
      showingNewKeyRef.current = false;
      // Clear forceKeyGeneration flag so RecoveryKeyModal can show again if needed
      sessionStorage.removeItem('forceKeyGeneration');
      // Don't set hasSSSSConfigured here - let the next page/visit determine correct state
      // If user came from RecoveryKeyModal, they should see it again
      // If user came from State B Reset, checkStatus will detect backup is active
    }
    onBack();
  }, [pendingResetKey, onBack]);

  // Loading state
  if (isCheckingStatus) {
    return (
      <div className="chat-backup-page">
        <div className="settings-modal-header">
          <button className="back-button" onClick={handleBack} aria-label={t('SignInForm.goBack')}>
            <ion-icon name="chevron-back-outline" />
          </button>
          <span className="settings-page-title">{t('ChatBackupPage.title')}</span>
        </div>
        <div className="chat-backup-content">
          <div className="chat-backup-loading">
            <div className="loading-spinner" />
            <span>{t('ChatBackupPage.loadingStatus')}</span>
          </div>
        </div>

        {/* Password dialog must be rendered even during loading for UIA */}
        <PasswordInputModal
          isOpen={showPasswordDialog}
          onConfirm={handlePasswordSubmit}
          onCancel={handlePasswordCancel}
          title={t('ChatBackupPage.passwordRequired')}
          message={t('ChatBackupPage.passwordMessage')}
          externalError={passwordError}
          confirmText={t('ChatBackupPage.submit')}
          cancelText={t('ChatBackupPage.cancel')}
          isSubmitting={isPasswordSubmitting}
        />
      </div>
    );
  }

  // Detection error state (e.g., timeout) - show retry UI
  if (detectionError) {
    return (
      <div className="chat-backup-page">
        <div className="settings-modal-header">
          <button className="back-button" onClick={handleBack} aria-label={t('SignInForm.goBack')}>
            <ion-icon name="chevron-back-outline" />
          </button>
          <span className="settings-page-title">{t('ChatBackupPage.title')}</span>
        </div>
        <div className="chat-backup-content">
          <div className="chat-backup-error" data-testid="detection-error">
            <div className="error-icon">
              <ion-icon name="warning-outline" />
            </div>
            <p className="error-message">{t('ChatBackupPage.detectionError')}</p>
            <p className="error-detail">{t('ChatBackupPage.detectionErrorDetail')}</p>
            <button
              className="retry-button"
              onClick={handleRetryDetection}
              data-testid="retry-button"
            >
              {t('ChatBackupPage.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-backup-page">
      <div className="settings-modal-header">
        <button className="back-button" onClick={handleBack} aria-label={t('SignInForm.goBack')}>
          <ion-icon name="chevron-back-outline" />
        </button>
        <span className="settings-page-title">{t('ChatBackupPage.title')}</span>
      </div>

      <div className="chat-backup-content">
        {hasSSSSConfigured ? (
          // State B: Backup is active
          <div className="chat-backup-active">
            <div className="chat-backup-success-icon">
              <ion-icon name="checkmark-circle" />
            </div>
            <h3 className="chat-backup-success-title">
              {t('ChatBackupPage.backupActiveTitle')}
            </h3>

            <div className="chat-backup-actions">
              <button
                className="chat-backup-btn chat-backup-btn-primary"
                onClick={handleExportKey}
                disabled={!generatedKey}
                data-testid="export-key-btn"
              >
                {t('ChatBackupPage.exportKeyButton')}
              </button>

              <button
                className="chat-backup-btn chat-backup-btn-secondary"
                onClick={handleResetKey}
                disabled={isResetting}
                data-testid="reset-key-btn"
              >
                {isResetting ? t('ChatBackupPage.resetting') : t('ChatBackupPage.resetKeyButton')}
              </button>
            </div>

            {!generatedKey && keyUnavailable && (
              <p className="chat-backup-export-note">
                <ion-icon name="information-circle-outline" />
                <span>{t('ChatBackupPage.keyUnavailableNote')}</span>
              </p>
            )}
            {!generatedKey && !keyUnavailable && (
              <p className="chat-backup-export-note">
                <ion-icon name="information-circle-outline" />
                <span>{t('ChatBackupPage.exportKeyNote')}</span>
              </p>
            )}
          </div>
        ) : (
          // State A: Create new backup key
          <div className={`chat-backup-create ${generatedKey ? 'animate-in' : ''}`}>
            <p className="chat-backup-description">
              {t('ChatBackupPage.description')}
            </p>

            {generatedKey && (
              <>
                <div className="recovery-key-display-box" data-testid="recovery-key-box">
                  <code className="recovery-key-code">{generatedKey}</code>
                </div>

                <div className="activation-section">
                  <h3 className="activation-section-heading">
                    {t('ChatBackupPage.saveKeyHeading')}
                  </h3>
                  <p className="activation-section-text">
                    {t('ChatBackupPage.saveKeyText')}
                  </p>
                  <div className="activation-buttons">
                    <button
                      className={`chat-backup-btn chat-backup-btn-primary ${showCopySuccess ? 'copy-success' : ''}`}
                      onClick={handleCopyToClipboard}
                      disabled={isActivating || ssssLoading || showCopySuccess || activationError}
                      data-testid="copy-to-clipboard-btn"
                    >
                      <ion-icon name={showCopySuccess ? 'checkmark' : 'clipboard-outline'} />
                      <span>{showCopySuccess ? t('ChatBackupPage.copied') : t('ChatBackupPage.copyToClipboardButton')}</span>
                    </button>
                    <button
                      className="chat-backup-btn chat-backup-btn-outline"
                      onClick={handleExportAndActivate}
                      disabled={isActivating || ssssLoading || !generatedKey || activationError}
                      data-testid="export-and-activate-btn"
                    >
                      <ion-icon name="download-outline" />
                      <span>{t('ChatBackupPage.exportRecoveryKeyButton')}</span>
                    </button>
                  </div>
                </div>

                <div
                  className={`activation-note ${isCopied ? 'success' : ''}`}
                  data-testid="activation-note"
                >
                  <strong>{t('ChatBackupPage.noteLabel')}</strong>
                  {' '}
                  {isCopied
                    ? t('ChatBackupPage.noteAfterCopy')
                    : t('ChatBackupPage.noteBeforeCopy')
                  }
                </div>

                {activationError && (
                  <div className="activation-error" role="alert">
                    <ion-icon name="alert-circle" />
                    <span>{activationError}</span>
                  </div>
                )}

                {isSSOAuthInProgress && (
                  <div className="activation-progress sso-auth-overlay">
                    <div className="loading-spinner" />
                    <span>{t('ChatBackupPage.ssoAuthInProgress', {}, 'Complete sign-in in the popup window...')}</span>
                  </div>
                )}

                {isActivating && !isSSOAuthInProgress && (
                  <div className="activation-progress">
                    <div className="loading-spinner" />
                    <span>{t('ChatBackupPage.activating')}</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Password dialog for UIA during cross-signing */}
      <PasswordInputModal
        isOpen={showPasswordDialog}
        onConfirm={handlePasswordSubmit}
        onCancel={handlePasswordCancel}
        title={t('ChatBackupPage.passwordRequired')}
        message={t('ChatBackupPage.passwordMessage')}
        externalError={passwordError}
        confirmText={t('ChatBackupPage.submit')}
        cancelText={t('ChatBackupPage.cancel')}
        isSubmitting={isPasswordSubmitting}
      />
    </div>
  );
};

ChatBackupPage.propTypes = {
  onBack: PropTypes.func.isRequired,
  onShowRecoveryKeyModal: PropTypes.func,
};

export default ChatBackupPage;
